<script setup>
import { ref, computed } from 'vue';

// 分时留电趋势: 今日 vs 对比日(昨日/上周同期)按小时(0-23, 北京时区)对比曲线。
// 与"每日留电趋势"同一套视觉语言: 今日=实线品牌色+面积, 对比日=虚线琥珀色;
// 悬停十字线 + 两日同时段数值 + 同时段涨跌。今日曲线截到当前小时(后面的小时
// 还没发生), 对比日整条 24 小时铺满, 两条按"小时"对齐共用 x 轴。
// 数据由父组件(Index.vue)传入, 随归属者/来源/域名筛选联动。

const props = defineProps({
  title: { type: String, required: true },
  subtitle: { type: String, default: '' },
  // 今日: 后端 today_hourly [{hour, count}] 只含有留电的小时(画到当前小时截断)
  primary: { type: Array, default: () => [] },
  primaryTotal: { type: Number, default: 0 },
  primaryDate: { type: String, default: '' },
  primaryLabel: { type: String, default: '今日' },
  // 对比日: 昨日/上周同期整 24 小时
  compare: { type: Array, default: () => [] },
  compareTotal: { type: Number, default: 0 },
  compareDate: { type: String, default: '' },
  compareLabel: { type: String, default: '昨日' },
  deltaLabel: { type: String, default: '较昨日同时段' },
});

// 北京时区"现在"(Index.vue 同款: UTC 毫秒 +8h 后按 wall-clock 读)
const bjNow = () => new Date(Date.now() + 8 * 3600000);

// 对比日: 补齐 0-23 整 24 个点
const compareData = computed(() => {
  const counts = {};
  props.compare.forEach(r => {
    counts[r.hour] = r.count;
  });
  return Array.from({ length: 24 }, (_, h) => ({ hour: h, count: counts[h] || 0 }));
});

// 今日: 0 点~当前小时(截断, 不画未来小时)
const primaryData = computed(() => {
  const currentHour = bjNow().getUTCHours();
  const counts = {};
  props.primary.forEach(r => {
    counts[r.hour] = r.count;
  });
  return Array.from({ length: currentHour + 1 }, (_, h) => ({
    hour: h,
    count: counts[h] || 0,
  }));
});

// 日期显示: "2026-09-09" → "9月9日"
const fmtCnDate = ds => {
  if (!ds) return '';
  const [, m, d] = ds.split('-');
  return `${Number(m)}月${Number(d)}日`;
};
const primaryLabelFull = computed(
  () => `${props.primaryLabel} ${fmtCnDate(props.primaryDate)}`
);
const compareLabelFull = computed(
  () => `${props.compareLabel} ${fmtCnDate(props.compareDate)}`
);

// 图表几何: 与每日趋势图同尺寸, 宽度 100% 自适应缩放
const CHART_W = 800;
const CHART_H = 240;
const PAD = { l: 40, r: 16, t: 14, b: 26 };
const plotW = CHART_W - PAD.l - PAD.r;
const plotH = CHART_H - PAD.t - PAD.b;

// x 轴固定 24 小时(0-23), 两日共用
const xAt = h => PAD.l + (h / 23) * plotW;

// y 轴整洁上限(1/2/2.5/5×10^k), 两日取共同上限才可比
const niceCeil = v => {
  if (v <= 4) return 4;
  const p = 10 ** Math.floor(Math.log10(v));
  return [1, 2, 2.5, 5, 10].find(k => k * p >= v) * p;
};
const yMax = computed(() =>
  niceCeil(
    Math.max(
      1,
      ...primaryData.value.map(p => p.count),
      ...compareData.value.map(p => p.count)
    )
  )
);
const yAt = v => PAD.t + plotH - (v / yMax.value) * plotH;

const linePath = computed(() =>
  primaryData.value
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${xAt(p.hour).toFixed(1)},${yAt(p.count).toFixed(1)}`)
    .join('')
);
const compareLinePath = computed(() =>
  compareData.value
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${xAt(p.hour).toFixed(1)},${yAt(p.count).toFixed(1)}`)
    .join('')
);
const areaPath = computed(() => {
  if (!primaryData.value.length) return '';
  const last = primaryData.value[primaryData.value.length - 1];
  const baseline = (PAD.t + plotH).toFixed(1);
  return `${linePath.value}L${xAt(last.hour).toFixed(1)},${baseline}L${xAt(0).toFixed(1)},${baseline}Z`;
});

// y 轴刻度(0/25%/50%/75%/100%), 数值加千分位
const yTicks = computed(() =>
  [0, 0.25, 0.5, 0.75, 1].map(f => ({
    y: yAt(f * yMax.value),
    label: Math.round(f * yMax.value).toLocaleString(),
  }))
);
// x 轴小时标签: 每 3 小时 + 末尾 23
const xLabels = computed(() => {
  const out = [];
  for (let h = 0; h <= 23; h += 3) out.push(h);
  if (out[out.length - 1] !== 23) out.push(23);
  return out;
});

// 悬停: 十字线 + 高亮点; 命中区是整个绘图区
const hoverHour = ref(-1);
const onChartMove = e => {
  const rect = e.currentTarget.getBoundingClientRect();
  const vx = ((e.clientX - rect.left) * CHART_W) / rect.width;
  const idx = Math.round(((vx - PAD.l) / plotW) * 23);
  hoverHour.value = Math.min(23, Math.max(0, idx));
};
const onChartLeave = () => {
  hoverHour.value = -1;
};
const onChartKey = e => {
  if (e.key === 'ArrowLeft') {
    hoverHour.value = Math.max(0, (hoverHour.value < 0 ? 24 : hoverHour.value) - 1);
    e.preventDefault();
  } else if (e.key === 'ArrowRight') {
    hoverHour.value = Math.min(23, hoverHour.value + 1);
    e.preventDefault();
  }
};

// 悬停数据: 今日点(超出当前小时时无值) + 对比日点 + 同时段涨跌
const hoverPoint = computed(() => {
  const h = hoverHour.value;
  if (h < 0) return null;
  const cur = primaryData.value.find(p => p.hour === h) || null;
  const prev = compareData.value[h];
  const anchor = cur || prev;
  // 同时段涨跌: 只在今日已发生该小时时显示
  let delta = null;
  if (cur && prev) {
    const diff = cur.count - prev.count;
    delta = {
      diff,
      pct: prev.count > 0 ? Math.round((diff / prev.count) * 100) : null,
    };
  }
  return { hour: h, cur, prev, delta, x: xAt(h), y: yAt(anchor.count) };
});
const fmtDelta = d => {
  if (!d || d.diff === 0) return '持平';
  const sign = d.diff > 0 ? '+' : '';
  const pct = d.pct !== null ? `（${d.pct > 0 ? '+' : ''}${d.pct}%）` : '';
  return `${sign}${d.diff}${pct}`;
};

// 提示框水平定位(%), 左右边缘夹住避免溢出
const tooltipLeft = computed(() =>
  hoverPoint.value
    ? Math.min(88, Math.max(12, (hoverPoint.value.x / CHART_W) * 100))
    : 0
);
const tooltipTop = computed(() =>
  hoverPoint.value ? (hoverPoint.value.y / CHART_H) * 100 : 0
);
</script>

<template>
  <div class="bg-n-solid-3 rounded-xl p-4 transition-opacity">
    <div class="mb-2 flex items-end justify-between flex-wrap gap-1">
      <div>
        <h2 class="text-sm font-semibold text-n-slate-12">{{ title }}</h2>
        <p class="text-xs text-n-slate-9 mt-0.5">
          {{ subtitle }}
        </p>
      </div>
      <!-- 图例: 今日实线(品牌色) vs 对比日虚线(琥珀色, 原始hex避开调色板缺色坑) -->
      <div class="flex items-center gap-3 text-xs text-n-slate-10 pb-0.5">
        <span class="flex items-center gap-1">
          <svg width="16" height="8" aria-hidden="true">
            <line
              x1="0"
              y1="4"
              x2="16"
              y2="4"
              class="stroke-n-brand"
              stroke-width="2"
            />
          </svg>
          {{ primaryLabelFull }} {{ primaryTotal.toLocaleString() }}
        </span>
        <span class="flex items-center gap-1">
          <svg width="16" height="8" aria-hidden="true">
            <line
              x1="0"
              y1="4"
              x2="16"
              y2="4"
              stroke="#d97706"
              stroke-width="2"
              stroke-dasharray="4 3"
            />
          </svg>
          {{ compareLabelFull }} {{ compareTotal.toLocaleString() }}
        </span>
      </div>
    </div>
    <div class="relative">
      <svg
        :viewBox="`0 0 ${CHART_W} ${CHART_H}`"
        class="w-full h-auto block"
        role="img"
        :aria-label="`${title}, ${primaryLabel}共${primaryTotal}条, ${compareLabel}共${compareTotal}条`"
        tabindex="0"
        @mousemove="onChartMove"
        @mouseleave="onChartLeave"
        @keydown="onChartKey"
      >
        <!-- 网格线 + y 轴刻度(细线, 退后) -->
        <line
          v-for="t in yTicks"
          :key="t.y"
          x1="40"
          :y1="t.y"
          :x2="CHART_W - 16"
          :y2="t.y"
          class="stroke-n-slate-4"
          stroke-width="1"
        />
        <text
          v-for="t in yTicks"
          :key="`label-${t.y}`"
          x="34"
          :y="t.y + 3"
          text-anchor="end"
          class="fill-n-slate-9 tabular-nums"
          font-size="10"
        >{{ t.label }}</text>
        <!-- x 轴小时标签(每3小时+23时) -->
        <text
          v-for="h in xLabels"
          :key="`x-${h}`"
          :x="xAt(h)"
          :y="CHART_H - 8"
          text-anchor="middle"
          class="fill-n-slate-9 tabular-nums"
          font-size="10"
        >{{ h }}时</text>
        <!-- 对比日曲线: 虚线琥珀色(画在底层, 今日实线压在上面) -->
        <path
          v-if="compareLinePath"
          :d="compareLinePath"
          fill="none"
          stroke="#d97706"
          stroke-width="2"
          stroke-dasharray="5 4"
          stroke-linejoin="round"
          stroke-linecap="round"
        />
        <!-- 今日面积淡填充 + 2px 折线 -->
        <path
          :d="areaPath"
          class="fill-n-brand"
          fill-opacity="0.08"
        />
        <path
          :d="linePath"
          fill="none"
          class="stroke-n-brand"
          stroke-width="2"
          stroke-linejoin="round"
          stroke-linecap="round"
        />
        <!-- 端点(当前小时): 品牌色点 + 2px 表面色描圈 -->
        <circle
          v-if="primaryData.length"
          :cx="xAt(primaryData[primaryData.length - 1].hour)"
          :cy="yAt(primaryData[primaryData.length - 1].count)"
          r="6"
          class="fill-n-solid-3"
        />
        <circle
          v-if="primaryData.length"
          :cx="xAt(primaryData[primaryData.length - 1].hour)"
          :cy="yAt(primaryData[primaryData.length - 1].count)"
          r="4"
          class="fill-n-brand"
        />
        <!-- 悬停十字线 + 两日各自高亮点 -->
        <g v-if="hoverPoint">
          <line
            :x1="hoverPoint.x"
            :y1="PAD.t"
            :x2="hoverPoint.x"
            :y2="PAD.t + plotH"
            class="stroke-n-slate-6"
            stroke-width="1"
          />
          <circle
            :cx="hoverPoint.x"
            :cy="yAt(hoverPoint.prev.count)"
            r="4"
            fill="#d97706"
            class="stroke-n-solid-3"
            stroke-width="2"
          />
          <circle
            v-if="hoverPoint.cur"
            :cx="hoverPoint.x"
            :cy="yAt(hoverPoint.cur.count)"
            r="4"
            class="fill-n-brand stroke-n-solid-3"
            stroke-width="2"
          />
        </g>
        <!-- 悬停命中区(整个绘图区, 大于折线本身) -->
        <rect
          :x="PAD.l"
          :y="PAD.t"
          :width="plotW"
          :height="plotH"
          fill="transparent"
        />
      </svg>
      <!-- 悬停数值框(今日/对比日两行对比; 纯文本绑定防注入) -->
      <div
        v-if="hoverPoint"
        class="absolute pointer-events-none -translate-x-1/2 -translate-y-[130%] bg-n-solid-2 border border-n-slate-5 rounded-lg px-2.5 py-1.5 shadow-md whitespace-nowrap"
        :style="{ left: `${tooltipLeft}%`, top: `${tooltipTop}%` }"
      >
        <div class="text-xs text-n-slate-9 mb-0.5">{{ hoverPoint.hour }}时</div>
        <div
          class="flex items-center gap-1.5 text-sm font-semibold text-n-slate-12 tabular-nums"
        >
          <span class="inline-block w-2.5 h-0.5 rounded bg-n-brand" />
          {{ primaryLabelFull }} {{ (hoverPoint.cur?.count ?? 0).toLocaleString() }}
        </div>
        <div
          class="flex items-center gap-1.5 text-sm font-medium tabular-nums"
          style="color: #d97706"
        >
          <span
            class="inline-block w-2.5 h-0.5 rounded"
            style="background: #d97706"
          />
          {{ compareLabelFull }} {{ (hoverPoint.prev?.count ?? 0).toLocaleString() }}
        </div>
        <!-- 同时段涨跌(涨绿跌红, 原始hex避开调色板缺色坑) -->
        <div
          v-if="hoverPoint.delta"
          class="flex items-center gap-1.5 text-xs font-medium tabular-nums mt-0.5"
          :style="{ color: hoverPoint.delta.diff >= 0 ? '#16a34a' : '#dc2626' }"
        >
          {{ deltaLabel }}
          <span class="font-semibold">{{ fmtDelta(hoverPoint.delta) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
